/* Impolie — previous / next arrows on the product image.
 *
 * The theme already knows how to change the main image: every thumbnail is a
 * button that does it. These arrows just click the right thumbnail, so the
 * behaviour, the zoom, the variant handling and the accessibility state all
 * stay exactly as the theme built them. Nothing about the gallery is replaced.
 *
 * Only the images for the colour currently selected are stepped through —
 * front, side, back — because the theme hides the rest, and a hidden thumbnail
 * has no box to measure.
 */
(function () {
  'use strict';

  var CSS = [
    '.imp-ga-host{position:relative}',
    '.imp-ga{position:absolute;top:var(--imp-ga-top,50%);transform:translateY(-50%);z-index:3;',
    'width:44px;height:44px;padding:0;border:0;border-radius:50%;background:rgba(251,249,246,.82);',
    'display:flex;align-items:center;justify-content:center;cursor:pointer;color:#1A1A1A;',
    'opacity:.9;transition:background .2s ease,opacity .2s ease}',
    '.imp-ga:hover{background:rgba(251,249,246,.99);opacity:1}',
    '.imp-ga:focus-visible{outline:2px solid #1A1A1A;outline-offset:2px}',
    '.imp-ga--prev{left:14px}',
    '.imp-ga--next{right:14px}',
    '.imp-ga svg{width:17px;height:17px;display:block}',
    '.imp-ga-off .imp-ga{display:none}',
    '@media(max-width:749px){.imp-ga{width:38px;height:38px}.imp-ga--prev{left:8px}.imp-ga--next{right:8px}.imp-ga svg{width:15px;height:15px}}',
    '@media(prefers-reduced-motion:reduce){.imp-ga{transition:none}}',
  ].join('');

  function injectCSS() {
    if (document.getElementById('imp-ga-css')) return;
    var s = document.createElement('style');
    s.id = 'imp-ga-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function onScreen(el) {
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function visibleThumbs(gallery) {
    return Array.prototype.filter.call(gallery.querySelectorAll('.thumbnail-list__item'), onScreen);
  }

  function visibleMedia(gallery) {
    return Array.prototype.filter.call(gallery.querySelectorAll('.product__media-item'), onScreen);
  }

  /* The theme presents the gallery two different ways, so the arrows have to
   * work two different ways:
   *
   *   desktop — one image at a time, changed by clicking a thumbnail
   *   mobile  — thumbnails hidden, images laid out in a swipeable strip
   *
   * On mobile there is no thumbnail to click, so the arrows scroll the strip by
   * one image instead, which is exactly what a swipe does. */
  function isSliderMode(gallery) {
    return visibleThumbs(gallery).length < 2 && visibleMedia(gallery).length > 1;
  }

  function arrow(dir, label) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'imp-ga imp-ga--' + dir;
    b.setAttribute('aria-label', label);
    var d = dir === 'prev' ? 'M13 2 5 9l8 7' : 'M5 2l8 7-8 7';
    b.innerHTML = '<svg viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="' + d + '" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return b;
  }

  function setup(gallery) {
    var host = gallery.closest('.product__media-wrapper') || gallery.parentElement;
    if (!host || host.querySelector('.imp-ga')) return;
    host.classList.add('imp-ga-host');

    var prev = arrow('prev', 'Previous image');
    var next = arrow('next', 'Next image');

    function step(dir) {
      var thumbs = visibleThumbs(gallery);

      if (thumbs.length > 1) {
        var at = 0;
        for (var i = 0; i < thumbs.length; i++) {
          if (thumbs[i].querySelector('[aria-current="true"]')) { at = i; break; }
        }
        var target = thumbs[(at + dir + thumbs.length) % thumbs.length];
        var btn = target.querySelector('button, a');
        if (btn) btn.click();        // the theme's own handler does the rest
        return;
      }

      // Mobile. The theme's own slider already has working prev/next controls
      // sitting under the image — press those rather than scrolling the strip
      // by hand, so paging, the counter and the scroll-snap all behave exactly
      // as they do when the theme's own control is used.
      var strip = gallery.querySelector('slider-component:not(.thumbnail-slider)');
      if (strip && !onScreen(strip)) strip = null;
      if (!strip) {
        var all = gallery.querySelectorAll('slider-component');
        for (var k = 0; k < all.length; k++) {
          if (!all[k].classList.contains('thumbnail-slider') && onScreen(all[k])) { strip = all[k]; break; }
        }
      }
      if (!strip) return;

      var own = strip.querySelector(dir > 0 ? '.slider-button--next' : '.slider-button--prev');
      if (own && !own.disabled) { own.click(); return; }

      // At the last image going forward (or the first going back) the theme
      // disables its own button. Wrap round to the other end.
      //
      // Clicking the opposite button once per image does not work here: the
      // strip scrolls smoothly, so every click after the first is measured
      // against a position that has not moved yet and is discarded, leaving the
      // customer stuck. Scroll the strip itself instead — the theme watches
      // that scroll and updates the counter and both buttons from it, exactly
      // as it does for a swipe.
      var list = strip.querySelector('.slider') || strip.querySelector('ul');
      if (!list) return;
      var end = dir > 0 ? 0 : Math.max(0, list.scrollWidth - list.clientWidth);
      try {
        list.scrollTo({ left: end, behavior: 'smooth' });
      } catch (e) {
        list.scrollLeft = end;              // older browsers
      }
    }

    prev.addEventListener('click', function () { step(-1); });
    next.addEventListener('click', function () { step(1); });
    host.appendChild(prev);
    host.appendChild(next);

    var queued = false;
    function place() {
      queued = false;
      var shown = visibleMedia(gallery);
      // one image for this colour, or none rendered yet: no arrows to show
      var canStep = visibleThumbs(gallery).length > 1 || shown.length > 1;
      host.classList.toggle('imp-ga-off', !canStep);

      // Centre on the picture itself, not on the whole column. The column also
      // contains the thumbnail strip, so centring on it drops the arrows below
      // the image. Prefer the active slide, then any visible slide, then the
      // viewer that holds them.
      var media = gallery.querySelector('.product__media-item.is-active');
      if (!media || !onScreen(media)) media = shown[0];
      if (!media || !onScreen(media)) media = gallery.querySelector('.product__media-list');
      if (!media) return;
      var hr = host.getBoundingClientRect(), mr = media.getBoundingClientRect();
      if (mr.height < 40) return;   // not laid out yet, try again next frame
      host.style.setProperty('--imp-ga-top', (mr.top - hr.top + mr.height / 2) + 'px');
    }
    function schedule() { if (!queued) { queued = true; requestAnimationFrame(place); } }

    place();
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    // the colour picker swaps which media are shown, and the theme moves
    // aria-current between thumbnails — both are attribute changes in here
    new MutationObserver(schedule).observe(gallery, {
      subtree: true, attributes: true, childList: true,
      attributeFilter: ['class', 'style', 'aria-current', 'hidden'],
    });
  }

  function boot() {
    var galleries = document.querySelectorAll('media-gallery');
    if (!galleries.length) return;
    injectCSS();
    Array.prototype.forEach.call(galleries, setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // section re-renders in the theme editor
  document.addEventListener('shopify:section:load', boot);
})();
